import { ENV_CONFIG } from 'app/common/config'
import { useDebounce } from 'app/hook'
import { handleDownloadExcel } from 'app/service/general/service'
import { useState } from 'react'
import { IFilterUnitList } from './type'

import { DashOutlined } from '@ant-design/icons'
import { SorterResult } from 'antd/es/table/interface'
import { DefineUnitType } from 'app/api/unit/constant'
import { IPosition, IUnit } from 'app/api/unit/model'
import R from 'app/assets/R'
import { PermissionEnum } from 'app/common/enum'
import { ISort } from 'app/common/interface'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { UNIT_MANAGEMENT_DETAILS_PATH } from 'app/components/router/route-path'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { usePagination } from 'app/hook'
import { useGetUnitList } from 'app/react-query/hook/unit'
import { IResponseQueryList } from 'app/react-query/model/common'
import { Pagination, initialPagination } from 'parkway-web-common'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { DeleteModalLayout, UpdateModalLayout } from './layouts'

export const useHook = () => {
  const navigate = useNavigate()

  const { t } = useTranslation()
  const { verifyPermission, user } = useVerifyPermission()
  const [filter, setFilter] = useState<IFilterUnitList>({})

  const filterDebounce = useDebounce(filter, 500)

  const onChangeFilter = (newFilter: IFilterUnitList) => {
    setFilter(newFilter)
  }

  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [sorts, setSort] = useState<ISort[]>([])
  const { flattenDataList } = usePagination()
  const { data, isLoading } = useGetUnitList({
    limit: pagination?.pageSize,
    page: pagination?.current,
    search: filterDebounce?.search,
    type: filterDebounce?.type,
    schoolId: filterDebounce?.schoolId,
    sorts,
  })

  const flattenData = useMemo(() => {
    const res: IResponseQueryList<IUnit[]> = flattenDataList(data)

    return res
  }, [data])

  const dataTable = useMemo(() => {
    return flattenData?.data
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? dataTable?.length
  }, [flattenData])

  const isHavePermissionUpdateUnit = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateAccount)
  }, [user])

  const isHavePermissionDeleteUnit = useMemo(() => {
    return verifyPermission(PermissionEnum.DeleteUnit)
  }, [user])

  const navigateToDetail = (unit?: IUnit) => {
    navigate(UNIT_MANAGEMENT_DETAILS_PATH, {
      state: {
        unit,
      },
    })
  }

  const columns = useMemo(() => {
    return [
      convertColumnTable<IUnit>({
        key: 'name',
        title: 'Tên đơn vị',
        classNameWidthColumnOverwrite: 'big-column',
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseButton type="text" onClick={() => navigateToDetail(record)}>
              <BaseText
                children={record?.name ?? '-'}
                fontWeight="semibold"
                colorText="collapseBackgroundColor"
              />
            </BaseButton>
          )
        },
      }),
      convertColumnTable<IUnit>({
        key: 'code',
        title: 'Mã đơn vị',
        sorter: true,
        classNameWidthColumnOverwrite: 'medium-column',
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.code ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<IUnit>({
        key: 'description',
        title: t(R.strings.description),
        classNameWidthColumnOverwrite: 'big-column',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.description ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<IUnit>({
        key: 'tradeUnionMembers',
        title: 'Số lượng đoàn viên',
        classNameWidthColumnOverwrite: 'medium-column',
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              colorText="statesGreenColor"
              children={record?.tradeUnionMembers?.length ?? 0}
            />
          )
        },
      }),

      convertColumnTable<IUnit>({
        key: 'type',
        title: 'Loại đơn vị',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={DefineUnitType?.[record?.type]}
            />
          )
        },
      }),

      convertColumnTable<IUnit>({
        key: 'school',
        title: 'Công đoàn cơ sở',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.school?.name}
            />
          )
        },
      }),

      // tổ trưởng
      convertColumnTable<IUnit>({
        key: 'positions',
        title: 'Lãnh đạo',
        classNameWidthColumnOverwrite: 'very-big-column',
        render: (_, record) => {
          const positionG = groupedPositions(record?.positions ?? [])
          return Object.keys(positionG)?.map(item => {
            const itemConverted = positionG?.[item]
            return (
              <BaseRow wrap={false} gutter={8}>
                <BaseCol xl={8}>
                  <BaseText children={`${item}:`} opacity="0.7" />
                </BaseCol>
                <BaseCol xl={12}>
                  {itemConverted?.map(itm => {
                    return (
                      <BaseText
                        children={`${itm?.fullName} [${itm?.mql}]`}
                        fontWeight="medium"
                        opacity="0.7"
                      />
                    )
                  })}
                </BaseCol>
              </BaseRow>
            )
          })
        },
      }),
      // action
      convertColumnTable<IUnit>({
        key: 'id',
        title: '',
        fixed: 'right',
        classNameWidthColumnOverwrite: 'number-column',
        render: (_, record) => {
          return (
            <BasePopover
              trigger="click"
              content={
                <BaseSpace>
                  {isHavePermissionUpdateUnit ? (
                    <UpdateModalLayout unit={record} />
                  ) : null}

                  {isHavePermissionDeleteUnit ? (
                    <DeleteModalLayout name={record?.name} id={record?.id} />
                  ) : null}
                </BaseSpace>
              }
            >
              <DashOutlined rev={undefined} rotate={90} />
            </BasePopover>
          )
        },
      }),
    ]
  }, [t])

  const handleTableChange = (
    pagination: Pagination,
    _,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const downloadExcel = async (): Promise<void> => {
    await handleDownloadExcel({
      params: {
        search: filterDebounce?.search,
        type: filterDebounce?.type,
        schoolId: filterDebounce?.schoolId,
        sorts,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}unit/download`,
      fileName: 'Đơn vị',
    })
  }
  return {
    isLoading,
    columns,
    pagination: {
      ...pagination,
      total,
    },
    dataTable,
    handleTableChange,
    filter,
    downloadExcel,
    onChangeFilter,
  }
}

const groupedPositions = (positions: IPosition[]) => {
  return positions.reduce((acc, position) => {
    const { name } = position
    if (!name) return acc
    if (!acc[name]) {
      acc[name] = []
    }
    acc[name].push(position.member)
    return acc
  }, {} as Record<string, IPosition['member'][]>)
}
